import { useEffect, useState } from 'react';

import blogCover from '@/assets/images/blog_01.png';
import { HEADLINE_FROM, HEADLINE_TO, HeroLights, useSectionReveal } from '@/components/marketing';
import { Skeleton } from '@/components/ui';

/**
 * Placeholder posts, in the spirit of the design's own.
 *
 * The design repeats four cards down three rows, so that is what ships until
 * there is a CMS to read from. Every card carries the one cover photograph
 * for now; give each post its own `image` and swap the copy and the dates for
 * real posts before launch.
 */
const POSTS = [
  {
    category: 'Founder story',
    date: 'Feb 12',
    read: 5,
    title: 'First step: Finding the right cofounder',
  },
  {
    category: 'Insights',
    date: 'Feb 8',
    read: 4,
    title: 'How AI is changing the way startups hire interns',
  },
  {
    category: 'Press',
    date: 'Feb 2',
    read: 3,
    title: 'Why the internship market has a matching problem',
  },
  {
    category: 'This & That',
    date: 'Jan 27',
    read: 6,
    title: 'Building a company profile Gen-Z talent trusts',
  },
];

const ROWS = 3;

/** The twelve the design shows: four across, three down. */
const ALL_POSTS = Array.from({ length: ROWS }, () => POSTS).flat();

/**
 * Stands in for the request until there is one.
 *
 * ponytail: a timer, not a fetch. Swap the body for `blogApi.list()` and the
 * skeleton, the arrival and everything else here keep working unchanged.
 */
function usePosts() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setPosts(ALL_POSTS), 1200);
    return () => clearTimeout(timer);
  }, []);

  return posts;
}

/**
 * "Welcome to our Wonder-ful blog."
 *
 * A heading in the hero's voice -- the same uppercase display cut, rising out
 * of its masks once on arrival -- over a grid of portrait cards. Each card is
 * its photograph: the meta and title sit on a gradient at its foot rather than
 * in a panel below it, which is what keeps twelve of them reading as a wall of
 * pictures instead of a table of entries.
 */
export function BlogPage() {
  const posts = usePosts();

  // Only the heading is on the section timeline. The cards are not in the
  // DOM when it is built -- they arrive with the data -- so each one carries
  // its own CSS rise, delayed by its place in the grid, and plays it the
  // moment it mounts.
  const root = useSectionReveal(
    ({ reveal }) => {
      reveal('.js-blog-line', HEADLINE_FROM, HEADLINE_TO, 0.1);
    },
    { start: 'top 80%', once: true },
  );

  return (
    <section ref={root} className="relative overflow-hidden pt-36 pb-24 lg:pt-44 lg:pb-32">
      <HeroLights />

      <div className="wonder-container wonder-measure relative">
        <h1 className="text-center text-[clamp(1.75rem,3.125vw,3.75rem)] leading-[1.3333] font-semibold tracking-[-0.03em] uppercase">
          <span className="block overflow-hidden pb-[0.08em] mb-[-0.08em]">
            <span className="js-blog-line text-on-night block">Welcome to</span>
          </span>{' '}
          <span className="block overflow-hidden pb-[0.08em] mb-[-0.08em]">
            {/* The second line is a lilac-to-lavender sweep clipped to the
                letters, as the design draws it. */}
            <span className="js-blog-line block bg-[linear-gradient(90deg,var(--color-iris-300),#DCC8FF)] bg-clip-text text-transparent">
              Our Wonder-ful blog
            </span>
          </span>
        </h1>

        <ul
          className="mt-16 grid gap-6 sm:grid-cols-2 lg:mt-24 lg:grid-cols-4 lg:gap-7"
          aria-busy={posts === null}
        >
          {posts === null
            ? ALL_POSTS.map((_, index) => (
                <li key={index}>
                  <PostSkeleton />
                </li>
              ))
            : posts.map((post, index) => (
                <li
                  key={index}
                  className="wonder-post-rise"
                  style={{ animationDelay: `${index * 90}ms` }}
                >
                  <PostCard {...post} />
                </li>
              ))}
        </ul>
      </div>
    </section>
  );
}

/** The card's silhouette -- frame, badge, two lines of copy -- while it loads. */
function PostSkeleton() {
  return (
    <div className="relative aspect-3/4 overflow-hidden rounded-2xl border border-[#EFE6F724] bg-white/4">
      <Skeleton className="absolute top-4 left-4 h-7 w-24 rounded-full bg-white/10" />
      <div className="absolute inset-x-0 bottom-0 p-5">
        <Skeleton className="h-4 w-28 bg-white/10" />
        <Skeleton className="mt-3 h-5 w-full bg-white/10" />
        <Skeleton className="mt-2 h-5 w-3/4 bg-white/10" />
      </div>
    </div>
  );
}

function PostCard({ category, date, read, title, image = blogCover }) {
  return (
    <article
      data-cursor="hover"
      className="group bg-night-deep relative aspect-3/4 overflow-hidden rounded-2xl border border-[#EFE6F724]"
    >
      <img
        src={image}
        alt=""
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.35]"
      />
      {/* The design's overlay: one #1B0028 gradient, solid at the foot, a
          fifth at the middle, gone at the top -- the copy's ground and the
          purple cast on the photo in a single layer. */}
      <div
        className="absolute inset-0 bg-[linear-gradient(0deg,#1B0028_0%,rgba(27,0,40,0.2)_50%,rgba(27,0,40,0)_100%)]"
        aria-hidden="true"
      />

      {/* Straight off the inspector: black at 49% under a 16px blur, with a
          1px white line on the top edge only -- a glint rather than a border. */}
      <span className="text-on-night absolute top-4 left-4 rounded-full border-t border-white/72 bg-black/49 px-3 py-1 text-sm leading-[1.18] font-normal backdrop-blur-lg">
        {category}
      </span>

      <div className="absolute inset-x-0 bottom-0 p-5">
        {/* Inspector: Inter 400, 16 on 16.5, white at 80%; the title Inter
            Tight 500, 20 on 26, white. */}
        <p className="text-base leading-none text-white/80">
          {date} · {read} min read
        </p>
        <h2 className="text-on-night mt-2 text-xl leading-[1.3] font-medium tracking-normal">
          {title}
        </h2>
      </div>
    </article>
  );
}
